import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button, Price } from '@/components/ui'
import { fugueLabProjects } from '@/data/portfolio'
import { Clarinet } from '@gjl/site-kit/art'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { capability, dfyBundle, managedService, oneTimeAddOn } from '@gjl/voice/pricing'

const launchBuild = oneTimeAddOn('launch-build')
const customBuild = oneTimeAddOn('bespoke-build')
const managedWebsite = managedService('managed-website')
const websiteCare = managedService('website-care')
const websiteSoftware = capability('website')!
const growthPartner = dfyBundle('growth-partner')

export default function WebsitesPage() {
  const breadcrumbs = generateBreadcrumbs('/services/websites', breadcrumbLabels)

  useSEO({
    title: 'Website Design & Development for Classical Music Organizations',
    description:
      'Modern, fast, mobile-optimized websites for festivals, ensembles, opera companies, choral organizations, and sacred music programs.',
    url: '/services/websites',
    canonical: '/services/websites',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Website Design & Development',
      description:
        'Custom websites for classical music organizations including festivals, ensembles, and choirs. Modern, fast, and mobile-optimized.',
      url: '/services/websites',
    }),
  })

  // Show all Fugue Lab projects
  const projects = fugueLabProjects

  // One entrance, on the hero only. The per-section staggered reveals and
  // hover lifts on every card were the stock kit; below the fold the page
  // now moves only in response to the visitor's own hover on a link.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-20 md:space-y-36 md:px-12 md:py-28">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Modern Websites for Classical Music Organizations</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your organization creates transformative musical experiences. But if your website is outdated, slow, or
          confusing, you&apos;re losing audiences, applicants, and donors before they ever engage.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          We build modern, fast, mobile-optimized websites for festivals, chamber ensembles, opera companies, choral
          organizations, and sacred music programs, websites that reflect the quality of your artistry and convert
          visitors into engaged participants.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
        </div>
        <ul className="text-muted-foreground space-y-1 pt-2 text-sm">
          <li>15-minute strategy call, no obligation</li>
          <li>Built by a working musician &amp; software engineer</li>
          <li>6-8 week timeline from kickoff to launch</li>
        </ul>
      </section>

      {/* Problem Section. Six symptoms as a typographic grid: the names carry
          it, so there is no tinted box or hover lift around each one. */}
      <section aria-labelledby="problems" className="space-y-12">
        <h2 id="problems" className="text-fluid-h3 max-w-3xl">
          Is Your Website Costing You Audiences, Applicants &amp; Donors?
        </h2>
        <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
          {PROBLEMS.map((item) => (
            <div key={item.problem} className="space-y-2">
              <h3 className="text-fluid-h4">{item.problem}</h3>
              <p className="text-muted-foreground max-w-[52ch] leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
        <p className="text-muted-foreground max-w-2xl italic">
          Sound familiar? You&apos;re not alone. Most organization websites were built years ago and haven&apos;t been
          updated to meet modern standards, or audience expectations.
        </p>
      </section>

      {/* Solution + pricing. The four options are not one ladder (two are
          one-time builds, two are monthly services), so they are set as two
          short typographic pairs rather than four boxed tiers. */}
      <section aria-labelledby="better-way" className="space-y-16">
        <div className="grid gap-6 md:grid-cols-12">
          <h2 id="better-way" className="text-fluid-h3 md:col-span-5">
            The Better Way: A Modern Website That Converts
          </h2>
          <p className="text-muted-foreground text-fluid-body max-w-[60ch] md:col-span-7">
            We build modern, fast, beautiful websites specifically for classical music organizations. Sites that look
            professional, work flawlessly on mobile, and make it easy for audiences to engage, whether buying tickets,
            applying to programs, or supporting your mission.
          </p>
        </div>

        <div className="space-y-12">
          <div className="space-y-3">
            <h3 className="text-fluid-h4">Website Pricing</h3>
            <p className="text-muted-foreground max-w-[60ch]">
              One flat build fee to get live, then simple monthly software. Full plans on the{' '}
              <Link
                to="/pricing"
                className="text-primary focus-visible:outline-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4"
              >
                pricing page
              </Link>
              .
            </p>
          </div>

          <div className="grid gap-x-12 gap-y-14 md:grid-cols-2">
            {/* Launch Build */}
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-primary text-sm font-medium">What most organizations choose</p>
                <h4 className="font-display text-2xl">{launchBuild.name}</h4>
                <Price size="lg">{launchBuild.price}</Price>
                <p className="text-muted-foreground">The fastest way to a professional site</p>
              </div>
              <ul className="text-muted-foreground space-y-1.5">
                <li>Custom-designed site</li>
                <li>Your content moved in</li>
                <li>Domain connected and live</li>
                <li>Mobile-optimized with SEO basics</li>
                <li>Editor access so you update it yourself</li>
              </ul>
              <p className="text-muted-foreground text-sm">Doubles as the onboarding fee for done-for-you plans.</p>
            </div>

            {/* Custom Build */}
            <div className="space-y-4 md:pt-6">
              <div className="space-y-1">
                <h4 className="font-display text-2xl">Custom build</h4>
                <Price size="lg">{customBuild.price}</Price>
                <p className="text-muted-foreground">For sites that need more than the standard build</p>
              </div>
              <ul className="text-muted-foreground space-y-1.5">
                <li>Fully custom design</li>
                <li>Complex integrations</li>
                <li>Festival and multi-program work</li>
                <li>Ticketing, donations, and applications</li>
              </ul>
              <p className="text-muted-foreground text-sm">Scoped and quoted together on a call.</p>
            </div>

            {/* Website Care */}
            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="font-display text-2xl">{websiteCare.name}</h4>
                <Price size="lg">${websiteCare.priceMonthly}/mo</Price>
                <p className="text-muted-foreground">For orgs that want one change a month handled</p>
              </div>
              <ul className="text-muted-foreground space-y-1.5">
                <li>One update request per month</li>
                <li>72-hour turnaround</li>
                <li>Website software included</li>
                <li>Editor access stays yours for everything else</li>
              </ul>
              <Link
                to="/services/done-for-you#website-care"
                className="text-primary focus-visible:outline-primary inline-block text-sm underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4"
              >
                See all done-for-you services
              </Link>
            </div>

            {/* Managed Website */}
            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="font-display text-2xl">{managedWebsite.name}</h4>
                <Price size="lg">${managedWebsite.priceMonthly}/mo</Price>
                <p className="text-muted-foreground">For orgs that never want to touch the site</p>
              </div>
              <ul className="text-muted-foreground space-y-1.5">
                <li>Unlimited update requests</li>
                <li>One active request at a time</li>
                <li>72-hour turnaround</li>
                <li>Website software included</li>
              </ul>
              <Link
                to="/services/done-for-you#managed-website"
                className="text-primary focus-visible:outline-primary inline-block text-sm underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4"
              >
                See all done-for-you services
              </Link>
            </div>
          </div>

          <p className="text-muted-foreground max-w-[70ch] text-sm leading-relaxed">
            Every build includes mobile optimization, SSL security, SEO basics, and editor training. Ongoing software
            starts at ${websiteSoftware.priceMonthly}/mo for hosting, editor, custom domain, forms, and SEO. Bundles are
            on the{' '}
            <Link
              to="/pricing"
              className="text-primary focus-visible:outline-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              pricing page
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Portfolio Section. Frameless, as on the home page: the screenshot is
          the proof, so it gets the room instead of a bordered card. */}
      {projects.length > 0 && (
        <section aria-labelledby="websites-built" className="space-y-12">
          <div className="grid gap-6 md:grid-cols-12">
            <h2 id="websites-built" className="text-fluid-h3 md:col-span-5">
              Websites We&apos;ve Built
            </h2>
            <p className="text-muted-foreground text-fluid-body max-w-[60ch] md:col-span-7">
              See how we&apos;ve helped classical music organizations modernize their digital presence and grow their
              audiences.
            </p>
          </div>
          <div className="grid gap-x-10 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) => (
              <Link
                key={project.slug}
                to={`/portfolio/${project.slug}`}
                aria-label={`View ${project.title} case study`}
                className="group focus-visible:outline-primary block space-y-4 focus-visible:outline-2 focus-visible:outline-offset-4"
              >
                <div className="bg-surface aspect-[3/2] overflow-hidden rounded-md">
                  {project.artwork === 'clarinet' ? (
                    <div className="clarinet-showcase h-full w-full">
                      <Clarinet />
                    </div>
                  ) : (
                    <img
                      src={project.image}
                      alt=""
                      width={600}
                      height={400}
                      loading="lazy"
                      style={project.imagePosition ? { objectPosition: project.imagePosition } : undefined}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02] motion-reduce:transition-none"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl group-hover:text-primary transition-colors duration-200">{project.title}</h3>
                  <p className="text-muted-foreground line-clamp-3 text-sm leading-relaxed">{project.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Benefits Section. Asymmetric 5/7 split: the heading holds the left
          column while the reasons run down the right. No icons; they only
          decorated the names. */}
      <section aria-labelledby="why-us" className="grid gap-10 md:grid-cols-12 md:gap-8">
        <h2 id="why-us" className="text-fluid-h3 md:col-span-5">
          Why Classical Music Organizations Choose Us
        </h2>
        <div className="grid gap-x-10 gap-y-10 sm:grid-cols-2 md:col-span-7">
          {BENEFITS.map((item) => (
            <div key={item.title} className="space-y-2">
              <h3 className="text-xl">{item.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section aria-labelledby="faq" className="space-y-12">
        <h2 id="faq" className="text-fluid-h3">
          Frequently Asked Questions
        </h2>
        <div className="space-y-10">
          {[
            {
              question: 'How long does it take to build a website?',
              answer:
                '6-8 weeks from kickoff to launch. We work in sprints: Discovery (2 weeks), Design & Development (3 weeks), Content Migration & Testing (2 weeks), Launch & Training (1 week).',
            },
            {
              question: 'Can we update the website ourselves after launch?',
              answer:
                "Yes! You can update concert calendars, programs, artist bios, and other content yourself through your website, no developer needed. We'll train your team on how to use it during launch week.",
            },
            {
              question: 'Do you work with organizations outside the US?',
              answer:
                "Absolutely. We've worked with organizations internationally. All we need is clear communication (Zoom calls work great across time zones) and content from your team.",
            },
            {
              question: 'What if we need ongoing support after launch?',
              answer: `Most organizations keep the software at $${websiteSoftware.priceMonthly}/mo and update the site themselves. If you never want to touch it, ${managedWebsite.name} at $${managedWebsite.priceMonthly}/mo gives you unlimited update requests, one active at a time, with a 72-hour turnaround. Pairing it with Managed Email brings the Growth Partner plan to ${growthPartner.price}. Full details are on our done-for-you services page.`,
            },
            {
              question: 'Can you integrate ticketing or donation systems?',
              answer:
                'Yes. We can integrate with most ticketing platforms (Eventbrite, PatronManager, etc.) and donation systems (Stripe, PayPal, Donorbox). If you need a custom system built, we can handle that as part of our Digital Systems package.',
            },
            {
              question: 'What makes you different from other web designers?',
              answer:
                "We're musicians serving musicians. Our founder is a professional organist and church music director who understands the classical music world. We speak your language and build systems tailored to your needs, not generic websites.",
            },
          ].map((item) => (
            <div key={item.question} className="grid gap-3 md:grid-cols-12 md:gap-8">
              <h3 className="text-xl md:col-span-5">{item.question}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section. Unboxed: the heading and the button are enough. */}
      <section aria-labelledby="cta" className="mx-auto max-w-3xl space-y-6 text-center">
        <h2 id="cta" className="text-fluid-h2">
          Ready to Modernize Your Website?
        </h2>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Schedule a free 15-minute digital audit. We&apos;ll review your current site, identify what&apos;s holding you
          back, and show you exactly how we can help.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
          <Link to="/contact">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Or Send Us a Message
              </Button>
            </m.div>
          </Link>
        </div>
        <p className="text-muted-foreground text-sm">
          No sales pitch. Just honest feedback on your website and clear next steps if you want our help.
        </p>
      </section>
    </div>
  )
}

const PROBLEMS = [
  {
    problem: 'Outdated Design',
    description:
      'Your website looks like it was built in 2010. Potential audiences question whether your organization is still active.',
  },
  {
    problem: 'Broken on Mobile',
    description:
      "Most visitors browse on phones. If your site doesn't work on mobile, they'll move on to another organization.",
  },
  {
    problem: 'Slow Load Times',
    description:
      'Your site takes 10+ seconds to load. Studies show 53% of visitors abandon sites that take longer than 3 seconds.',
  },
  {
    problem: 'Confusing Navigation',
    description:
      "Visitors can't find your concert calendar, ticket information, or donation page. They give up and leave.",
  },
  {
    problem: 'Impossible to Update',
    description:
      "You need to hire a developer every time you want to update your concert schedule or artist bios. It's expensive and slow.",
  },
  {
    problem: 'No Clear Call to Action',
    description:
      "There's no obvious path for visitors to buy tickets, donate, or sign up. Interested people don't know what to do next.",
  },
]

const BENEFITS = [
  {
    title: 'Built by Musicians',
    description:
      "We're not generic web developers. Our founder is a professional organist and church music director who understands the classical music world from the inside.",
  },
  {
    title: 'Fast & Modern',
    description:
      'Built on Next.js: the same technology used by Hulu, Netflix, and TikTok. Your site will be lightning-fast and future-proof.',
  },
  {
    title: 'Mobile-First Design',
    description:
      'Modern audiences browse on phones. We design for mobile first, then adapt for desktop, so nothing breaks on the screen they actually use.',
  },
  {
    title: 'Easy to Update',
    description:
      'Update your concert calendar, artist bios, and program information yourself through your website. No developer needed for routine content changes.',
  },
  {
    title: 'Conversion-Focused',
    description:
      'Every page is designed with clear goals: get audiences to buy tickets, donate, apply, or subscribe. Clear CTAs, intuitive navigation, and obvious next steps.',
  },
  {
    title: 'White-Glove Service',
    description:
      "You'll work directly with Garrett (founder). No offshore teams, no project managers who don't understand music.",
  },
]
